import {
    Alert,
    Button,
    Group,
    Modal,
    NumberInput,
    Radio,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { listVendorTermsOptions, vendorTermsOptionsError } from '@diagon-alley/common';
import { Warning } from '@phosphor-icons/react';
import { type FC, useEffect, useState } from 'react';

import {
    updateVendorPaymentTerms,
    type VendorListItem,
    type VendorPaymentTermsConfig,
    type VendorTermsOption,
} from '@api/vendors';

import { TermsRows } from '../orders/TermsRows';
import {
    draftsFromTemplate,
    nextRow,
    templateFromDrafts,
    type TrancheDraft,
} from '../orders/termsRows.model';
import { textColors } from '../procurementOrders/shared';
import classes from './VendorTermsModal.module.css';

/**
 * A vendor's payment terms (Settings → Entities → Vendors). A vendor can offer several sets -
 * "Pre-dispatch", "60-day credit" - each with a name, and whoever places an order picks one.
 * For credit-partner rows this edits the financing config instead.
 *
 * Orders copy the terms they are placed on, so changes here apply to FUTURE orders only.
 */

type OptionDraft = {
    id: string;
    name: string;
    country: string;
    isDefault: boolean;
    minOrderValue: number | '';
    minOrderQty: number | '';
    tranches: TrancheDraft[];
};

const toDraft = (option: VendorTermsOption): OptionDraft => ({
    id: option.id,
    name: option.name,
    country: option.country ?? '',
    isDefault: !!option.isDefault,
    minOrderValue: option.minOrderValue ?? '',
    minOrderQty: option.minOrderQty ?? '',
    tranches: draftsFromTemplate(option.tranches),
});

const newOption = (first: boolean): OptionDraft => {
    const advance = nextRow([]);
    return {
        id: crypto.randomUUID(),
        name: first ? 'Standard' : '',
        country: '',
        isDefault: first,
        minOrderValue: '',
        minOrderQty: '',
        tranches: [advance, nextRow([advance])],
    };
};

/** Options as they will be saved: an option nobody filled in is not an option. */
const toOptions = (drafts: OptionDraft[]): VendorTermsOption[] =>
    drafts
        .filter((d) => d.name.trim() || d.tranches.some((t) => Number(t.percentage) > 0))
        .map((d) => ({
            id: d.id,
            name: d.name.trim(),
            country: d.country.trim() || null,
            isDefault: d.isDefault,
            minOrderValue: d.minOrderValue === '' ? null : Number(d.minOrderValue),
            minOrderQty: d.minOrderQty === '' ? null : Number(d.minOrderQty),
            tranches: templateFromDrafts(d.tranches),
        }));

export const VendorTermsModal: FC<{
    vendor: VendorListItem;
    opened: boolean;
    onClose: () => void;
    onSaved: () => void;
}> = ({ vendor, opened, onClose, onSaved }) => {
    const isCreditPartner = vendor.vendorRole === 'Credit partner';
    const [options, setOptions] = useState<OptionDraft[]>([]);
    const [creditDays, setCreditDays] = useState<number | ''>('');
    const [premiumPct, setPremiumPct] = useState<number | ''>('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!opened) return;
        const cfg = vendor.paymentTermsConfig;
        const saved = listVendorTermsOptions(cfg).map(toDraft);
        setOptions(saved.length > 0 ? saved : [newOption(true)]);
        setCreditDays(cfg?.financing?.creditDays ?? '');
        setPremiumPct(cfg?.financing?.premiumPct ?? '');
        setError(null);
    }, [opened, vendor]);

    const update = (id: string, patch: Partial<OptionDraft>): void =>
        setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));

    const markDefault = (id: string): void =>
        setOptions((prev) => prev.map((o) => ({ ...o, isDefault: o.id === id })));

    const buildConfig = (): VendorPaymentTermsConfig | null | string => {
        if (isCreditPartner) {
            if (creditDays === '' || premiumPct === '') {
                return 'Credit days and premium % are both required.';
            }
            return {
                ...(vendor.paymentTermsConfig ?? {}),
                financing: { creditDays: Number(creditDays), premiumPct: Number(premiumPct) },
            };
        }
        const built = toOptions(options);
        if (built.length === 0) return null;
        // With one set there is nothing to choose between: it is the default.
        const toSave =
            built.length === 1 ? [{ ...built[0], isDefault: !built[0].country }] : built;
        const invalid = vendorTermsOptionsError(toSave);
        if (invalid) return invalid;
        if (toSave.length > 1 && !toSave.some((o) => o.isDefault)) {
            return 'Choose which terms are the default.';
        }
        return { options: toSave };
    };

    const handleSave = async (): Promise<void> => {
        setError(null);
        const config = buildConfig();
        if (typeof config === 'string') {
            setError(config);
            return;
        }
        setSaving(true);
        try {
            await updateVendorPaymentTerms(vendor.vendorId, config);
            onSaved();
            onClose();
        } catch (e) {
            setError((e as Error)?.message || 'Could not save — please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={`Payment terms — ${vendor.name}`}
            size="xl"
            zIndex={3000}
        >
            <Stack gap={16}>
                <Text size="13px" c={textColors.secondary} lh={1.55}>
                    {isCreditPartner
                        ? 'The financing this partner offers. Changes apply to future orders only.'
                        : 'Each set of terms has a name. With one set, new orders are placed on it without asking. With two or more, whoever places the order picks one. Changes apply to future orders only; existing orders keep the terms they were agreed under.'}
                </Text>

                {isCreditPartner ? (
                    <Group gap={12} align="flex-end">
                        <NumberInput
                            label="Credit days (after dispatch)"
                            size="xs"
                            w={180}
                            min={0}
                            max={365}
                            value={creditDays}
                            onChange={(v) => setCreditDays(typeof v === 'number' ? v : '')}
                        />
                        <NumberInput
                            label="Premium %"
                            size="xs"
                            w={120}
                            min={0}
                            max={100}
                            step={0.1}
                            decimalScale={2}
                            value={premiumPct}
                            onChange={(v) => setPremiumPct(typeof v === 'number' ? v : '')}
                        />
                    </Group>
                ) : (
                    <>
                        {options.map((option) => (
                            <div key={option.id} className={classes.option}>
                                <div className={classes.optionHead}>
                                    <TextInput
                                        className={classes.name}
                                        size="xs"
                                        label="Name"
                                        withAsterisk
                                        placeholder="e.g. 60-day credit"
                                        value={option.name}
                                        onChange={(e) =>
                                            update(option.id, { name: e.currentTarget.value })
                                        }
                                    />
                                    <TextInput
                                        className={classes.country}
                                        size="xs"
                                        label="Only for country"
                                        placeholder="Any (optional)"
                                        value={option.country}
                                        onChange={(e) =>
                                            update(option.id, {
                                                country: e.currentTarget.value,
                                                // A default hidden from other countries would
                                                // be a default nobody is offered.
                                                isDefault: e.currentTarget.value.trim()
                                                    ? false
                                                    : option.isDefault,
                                            })
                                        }
                                    />
                                    <NumberInput
                                        size="xs"
                                        w={120}
                                        label="Min order value"
                                        placeholder="Any"
                                        title="Offered only on an order worth at least this much (order currency)"
                                        min={0}
                                        hideControls
                                        value={option.minOrderValue}
                                        onChange={(v) => update(option.id, { minOrderValue: typeof v === 'number' ? v : '' })}
                                    />
                                    <NumberInput
                                        size="xs"
                                        w={100}
                                        label="Min units"
                                        placeholder="Any"
                                        title="Offered only on an order of at least this many units"
                                        min={0}
                                        hideControls
                                        value={option.minOrderQty}
                                        onChange={(v) => update(option.id, { minOrderQty: typeof v === 'number' ? v : '' })}
                                    />
                                    <Radio
                                        className={classes.defaultPick}
                                        size="xs"
                                        label="Default"
                                        checked={option.isDefault}
                                        disabled={!!option.country.trim()}
                                        onChange={() => markDefault(option.id)}
                                    />
                                    {options.length > 1 && (
                                        <Button
                                            size="compact-xs"
                                            variant="subtle"
                                            color="red"
                                            onClick={() =>
                                                setOptions((prev) =>
                                                    prev.filter((o) => o.id !== option.id),
                                                )
                                            }
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </div>
                                <TermsRows
                                    tranches={option.tranches}
                                    onChange={(tranches) => update(option.id, { tranches })}
                                />
                            </div>
                        ))}
                    </>
                )}

                {error ? (
                    <Alert color="red" icon={<Warning size={16} />} p={10}>
                        <Text size="12px">{error}</Text>
                    </Alert>
                ) : null}

                <Group justify="space-between" gap={8}>
                    {isCreditPartner ? (
                        <span />
                    ) : (
                        <Button
                            size="xs"
                            variant="light"
                            color="violet"
                            onClick={() =>
                                setOptions((prev) => [...prev, newOption(prev.length === 0)])
                            }
                        >
                            + Add another set of terms
                        </Button>
                    )}
                    <Group gap={8}>
                        <Button size="xs" variant="default" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button size="xs" loading={saving} onClick={handleSave}>
                            Save terms
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
};
